import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaBars } from 'react-icons/fa';
import { useTheme } from '../App'; // 🔥 Import the global theme hook

/* ================= STYLED COMPONENTS ================= */
const NavWrapper = styled.nav`
  height: 80px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 8%;
  position: fixed;
  top: 0;
  z-index: 99;
  
  /* 🔥 Theme applied to background and border */
  background-color: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(15, 23, 42, 0.96)' : 'rgba(255, 255, 255, 0.96)'};
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'};
  transition: all 0.3s ease;

  &.navbarScroll {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`;

const Logo = styled(NavLink)`
  font-size: 25px;
  font-weight: 700;
  color: ${({ $isDarkMode }) => $isDarkMode ? '#f8fafc' : '#0f172a'};
  text-decoration: none;

  span {
    color: ${({ $isDarkMode }) => $isDarkMode ? '#f8fafc' : '#0f172a'};
    font-family: "Playfair", serif;
  }
`;

const Menu = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  @media screen and (max-width: 1023px) {
    position: absolute;
    top: 80px;
    left: ${props => (props.showNav ? '0' : '-100%')};
    width: 100%;
    
    /* 🔥 Theme applied to mobile menu background */
    background-color: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(15, 23, 42, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
    backdrop-filter: blur(8px);
    border-top: 1px solid ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
    transition: left 0.3s ease-in-out;
    padding: 20px 0;
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
  }

  @media screen and (min-width: 1024px) {
    display: flex;
    align-items: center;
  }
`;

const MenuItem = styled.li`
  /* 🔥 Theme applied to text color */
  color: ${({ $isDarkMode }) => $isDarkMode ? '#cbd5e1' : '#334155'};
  font-weight: 600;
  font-family: "Poppins", sans-serif;
  font-size: 15px;
  position: relative;
  cursor: pointer;
  transition: color 0.2s ease;

  @media screen and (max-width: 1023px) {
    display: block;
    padding: 20px 15%;
    border-bottom: 1px solid ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
    font-size: 18px;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(59, 130, 246, 0.1)' : '#f8fafc'};
      color: #3b82f6;
    }
  }

  @media screen and (min-width: 1024px) {
    margin: 0 20px;

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: -12px;
      transform: translateY(-50%);
      height: 5px;
      width: 5px;
      border-radius: 50%;
      /* 🔥 Theme applied to the little dot next to menu items */
      background-color: ${({ $isDarkMode }) => $isDarkMode ? '#475569' : '#cbd5e1'};
      transition: background-color 0.2s ease;
    }

    &:hover {
      color: #3b82f6;
    }

    &:hover::after {
      background-color: #3b82f6;
    }
  }
`;

const Dropdown = styled.div`
  display: ${props => (props.isOpen ? 'block' : 'none')};
  position: absolute;
  left: 0;
  top: 100%;
  background-color: ${({ $isDarkMode }) => $isDarkMode ? '#1e293b' : '#ffffff'};
  min-width: 180px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 10;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${({ $isDarkMode }) => $isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'};

  a {
    color: ${({ $isDarkMode }) => $isDarkMode ? '#cbd5e1' : '#334155'};
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover {
      background-color: ${({ $isDarkMode }) => $isDarkMode ? 'rgba(59, 130, 246, 0.1)' : '#f8fafc'};
      color: #3b82f6;
    }
  }

  @media screen and (max-width: 1023px) {
    position: static;
    box-shadow: none;
    background-color: transparent;
    border: none;
    padding-left: 15%;
  }
`;

const NavBtn = styled.div`
  display: none;

  @media screen and (max-width: 1023px) {
    display: flex;
    align-items: center;
  }
`;

// 🔥 Modified to respect dark mode reds
const LogoutButton = styled.span`
  color: ${({ $isDarkMode }) => $isDarkMode ? '#ef4444' : '#dc2626'};
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: 1023px) {
    font-size: 18px;
  }

  &:hover {
    color: ${({ $isDarkMode }) => $isDarkMode ? '#f87171' : '#b91c1c'};
  }
`;

// 🔥 Re-styled to match the modern look of the normal Staff Navbar
const Bars = styled.button`
  border: none;
  background-color: ${({ $isDarkMode }) => $isDarkMode ? '#1e293b' : '#f8fafc'};
  color: ${({ $isDarkMode }) => $isDarkMode ? '#cbd5e1' : '#334155'};
  height: 42px;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;

  &:hover {
    background-color: ${({ $isDarkMode }) => $isDarkMode ? '#334155' : '#e2e8f0'};
    transform: scale(0.95);
  }
`;

/* ================= COMPONENT ================= */
const NavBarSuper = ({ user, setUser }) => {
  const [showNav, setShowNav] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const isMobileOrTablet = useMediaQuery({ maxWidth: 1023 });
  const navigate = useNavigate();

  // 🔥 Hook into the global theme
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        if (window.scrollY > 100) {
          navRef.current.classList.add('navbarScroll');
        } else {
          navRef.current.classList.remove('navbarScroll');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
        setShowNav(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (menu) => {
    setOpenDropdown(openDropdown === menu ? null : menu);
  };

  const closeMenu = () => {
    if (isMobileOrTablet) {
      setShowNav(false);
      setOpenDropdown(null);
    }
  };

  const menuHandler = () => {
    setShowNav(prev => !prev);
    setOpenDropdown(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/');
    window.location.reload();
  };

  return (
    <NavWrapper ref={navRef} $isDarkMode={isDarkMode}>
      {/* 🔥 Inline style text color responds to theme */}
      <div style={{ fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.03em", color: isDarkMode ? "#f8fafc" : "#0f172a" }}>
        <span style={{ color: "#3b82f6" }}>Performance</span>Pro
      </div>
      
      <Menu ref={menuRef} showNav={showNav} $isDarkMode={isDarkMode}>
        <MenuItem $isDarkMode={isDarkMode}>
          <LogoutButton onClick={handleLogout} $isDarkMode={isDarkMode}>
            Log Out
          </LogoutButton>
        </MenuItem>
      </Menu>

      <NavBtn>
        <Bars onClick={menuHandler} $isDarkMode={isDarkMode}>
          <FaBars size={20} color={isDarkMode ? "#cbd5e1" : "#334155"} />
        </Bars>
      </NavBtn>
    </NavWrapper>
  );
};

export default NavBarSuper;